import React from 'react';
import { MiniWheel } from '../taskometer/WheelView.jsx';
import { FAMOUS_WHEELS } from '../defaults/famousWheels';
import { ARCHETYPES, ARCHETYPE_WHEELS } from '../defaults/scheduleArchetypes';
import '../taskometer/taskometer.css';
import './marketing.css';

// Build a quick lookup so the archetype showcase can preview the
// wheel for each archetype without reaching into the app's state.
const WHEEL_BY_ID = (() => {
  const m = new Map();
  for (const w of ARCHETYPE_WHEELS) m.set(w.id, w);
  for (const w of FAMOUS_WHEELS) if (!m.has(w.id)) m.set(w.id, w);
  return m;
})();

/**
 * Marketing landing page. Lives at `/`. Two CTAs: try the app free,
 * or look at the team product. Uses the same paper aesthetic as the app
 * so the brand reads consistent across surfaces.
 */
export default function Landing() {
  // Pick three recognizable wheels for the hero strip — Buffett (CEO),
  // Pomodoro (system), Franklin (historical). Real shapes, not faked.
  const showcase = ['famous_buffett', 'system_pomodoro', 'famous_franklin']
    .map(id => FAMOUS_WHEELS.find(w => w.id === id))
    .filter(Boolean);

  return (
    <div className="tm-paper mk-page">
      <header className="mk-nav">
        <a href="/" className="mk-brand">taskometer</a>
        <nav className="mk-nav-links">
          <a href="#how">How it works</a>
          <a href="#shapes">Schedules</a>
          <a href="/teams">For teams</a>
          <a href="/app" className="tm-btn tm-primary tm-sm mk-cta-link">Open app →</a>
        </nav>
      </header>

      {/* HERO ──────────────────────────────────────────────────────────── */}
      <section className="mk-hero">
        <div className="mk-hero-text">
          <h1 className="mk-h1">
            Find your rhythm.<br/>
            <span className="mk-h1-accent">Find your people.</span>
          </h1>
          <p className="mk-lede">
            Pick the archetype that sounds like you — Early Bird, Night
            Owl, Parent, Freelancer — and start with a day schedule that
            already makes sense. Tweak it. Share it. See how others on
            the same rhythm structure their days.
          </p>
          <div className="mk-cta-row">
            <a href="/app" className="tm-btn tm-primary mk-cta">
              Open the app →
            </a>
            <a href="/teams" className="tm-btn tm-ghost mk-cta">
              For teams →
            </a>
          </div>
          <div className="mk-mono mk-fineprint">
            free forever for solo · team plans for shared rhythms
          </div>
        </div>

        <div className="mk-hero-art">
          <div className="mk-hero-bigwheel">
            <MiniWheel
              slots={(showcase[0]?.blocks || []).map(b => ({
                startTime: b.startTime, endTime: b.endTime, color: b.color,
              }))}
              size={280}
              thickness={32}
            />
            <div className="mk-hero-bigwheel-label">{showcase[0]?.name || 'Workday'}</div>
          </div>
          <div className="mk-hero-mini">
            {showcase.slice(1).map(w => (
              <div key={w.id} className="mk-hero-mini-card">
                <MiniWheel
                  slots={(w.blocks || []).map(b => ({
                    startTime: b.startTime, endTime: b.endTime, color: b.color,
                  }))}
                  size={84}
                  thickness={11}
                />
                <span className="mk-mono mk-hero-mini-name">{w.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS ──────────────────────────────────────────────────── */}
      <section id="how" className="mk-section">
        <h2 className="mk-h2">Three moves. That's it.</h2>
        <div className="mk-steps">
          <Step
            n="01"
            title="Capture as you go"
            body="One line. Press n from anywhere, type the thing, hit Enter. No type, no time, no block — your inbox holds it until you're ready."
          />
          <Step
            n="02"
            title="Plan when you sit down"
            body="Drag from the inbox onto a colored block in your day. Done in seconds. The skeleton's already there — Workday on weekdays, Weekend on Sat/Sun."
          />
          <Step
            n="03"
            title="Swap schedules when life shifts"
            body="Pick a different schedule to repaint a day. Override a sick day. Save today's blocks as a new schedule and apply it across a range."
          />
        </div>
      </section>

      {/* ARCHETYPE SHOWCASE ───────────────────────────────────────────── */}
      <section id="shapes" className="mk-section">
        <div className="mk-section-head">
          <h2 className="mk-h2">Pick the archetype that's you.</h2>
          <p className="mk-mono mk-section-sub">
            Eight rhythms, one click each. Tweak everything afterwards. Famous-people
            schedules (Buffett, Mozart, Kafka) live as a curated easter egg inside the app.
          </p>
        </div>
        <div className="mk-grid">
          {ARCHETYPES.map(a => {
            const wheel = WHEEL_BY_ID.get(a.wheelId);
            if (!wheel) return null;
            return (
              <a key={a.id} href="/app" className="mk-grid-card" style={{ borderColor: 'var(--rule)' }}>
                <MiniWheel
                  slots={(wheel.blocks || []).map(b => ({
                    startTime: b.startTime, endTime: b.endTime, color: b.color,
                  }))}
                  size={72}
                  thickness={9}
                />
                <div className="mk-grid-card-text">
                  <div className="mk-grid-card-name">
                    <span aria-hidden style={{ marginRight: 4 }}>{a.icon}</span>
                    {a.name}
                  </div>
                  <div className="mk-mono mk-grid-card-cat" style={{
                    textTransform: 'none',
                    letterSpacing: 'normal',
                    color: 'var(--ink-soft)',
                    fontSize: 11,
                    lineHeight: 1.35,
                  }}>
                    {a.blurb}
                  </div>
                </div>
              </a>
            );
          })}
        </div>
        <div style={{ textAlign: 'center', marginTop: 18 }}>
          <a href="/app" className="tm-btn tm-primary mk-cta">
            Pick yours and start →
          </a>
        </div>
      </section>

      {/* TEAMS TEASER ──────────────────────────────────────────────────── */}
      <section className="mk-section mk-teams-teaser">
        <div>
          <div className="mk-mono mk-eyebrow">For teams</div>
          <h2 className="mk-h2">Your team's rhythm, in one schedule.</h2>
          <p className="mk-lede">
            Shared schedules. Outlook two-way sync. A dashboard showing where
            the team's time actually went. No more "Tuesday is no-meeting
            day" buried in a Slack pinned message.
          </p>
          <a href="/teams" className="tm-btn tm-primary mk-cta">
            See the team dashboard →
          </a>
        </div>
        <div className="mk-teams-art">
          <MiniWheel
            slots={(FAMOUS_WHEELS.find(w => w.id === 'famous_cook')?.blocks || []).map(b => ({
              startTime: b.startTime, endTime: b.endTime, color: b.color,
            }))}
            size={200}
            thickness={22}
          />
        </div>
      </section>

      <footer className="mk-footer">
        <div className="mk-mono">© taskometer · capture as you go, plan when you sit down</div>
        <div className="mk-footer-links">
          <a href="/app">App</a>
          <a href="/teams">Teams</a>
          <a href="/pricing">Pricing</a>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
        </div>
      </footer>
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <div className="mk-step">
      <div className="mk-step-n">{n}</div>
      <div className="mk-step-title">{title}</div>
      <div className="mk-step-body">{body}</div>
    </div>
  );
}
